import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useParams, useSearchParams } from 'react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  getProject,
  getActivity,
  getTasks,
  getPermits,
  getProjectAlbums,
  getProjectPhotos,
  portalPhotoRawUrl,
  PortalApiError,
  // D-130 — client photo albums
  listClientAlbums,
  createClientAlbum,
  patchClientAlbum,
  deleteClientAlbum,
  listClientPhotos,
  deleteClientPhoto,
  uploadPortalPhoto,
  clientPhotoRawUrl,
  type ClientAlbum,
  type ClientPhoto,
  type PortalActivityEntry,
  type PortalAlbum,
  type PortalPhase,
  type PortalPhoto,
  type PortalProject,
  type PortalTask,
} from '../lib/portalApi';
import { PinGate } from '../components/PinGate';
// D-129 — UploadSection import removed; client upload widget is OFF
// the PM-photos Photos tab until the dedicated "Client photo albums"
// surface ships (D-130).

// D-130 — three portal tabs now: Overview / Photos / My photos.
type PortalTab = 'overview' | 'photos' | 'my-photos';

// D-122 (HARD #2) — the portal now renders the SAME ProjectPage component
// the PM uses on the Overview, in `viewMode="portal"`. ProjectPage lives in
// the main Conduit client at ../../../client/src/pages/ProjectPage.tsx and
// is imported via the `@conduit-client` Vite path alias (see vite.config.ts).
//
// The data layer is the bridge: portalApi returns the D1-shaped published
// subset, ProjectPage's internal useQuery hooks expect the local SQLite
// shapes. This file pre-fills the React Query cache with the mapped data
// before ProjectPage mounts, and sets staleTime: Infinity on the relevant
// keys so the queryFns inside ProjectPage never fire (they'd hit
// /api/projects/:id which doesn't exist on the portal Worker). Belt and
// suspenders: portal-mode renders gate every mutation/refetch trigger
// behind `!isPortal`, so even an accidental cache invalidation would have
// no user-visible effect.
import ProjectPage from '@conduit-client/pages/ProjectPage';
import type {
  ActivityLogEntry,
  PhaseSummary,
  ProjectDetail,
  ProjectDetailResponse,
  Task,
  TasksListResponse,
  ActivityLogListResponse,
  ProjectType,
  ColorTag,
} from '@conduit-client/lib/api';

/**
 * Portal — the project page served at projects.roughinadvisory.com/<slug>.
 *
 * Render pipeline:
 *   1. PIN gate (existing) — gated by Worker 401 with `require_pin: true`
 *   2. Fetch via portalApi: project + activity + tasks + permits in parallel
 *   3. Map every D1-shaped row into the SQLite shapes ProjectPage expects
 *   4. Pre-fill the TanStack Query cache for every ProjectPage useQuery key
 *      and pin staleTime to Infinity for those keys
 *   5. Mount <ProjectPage viewMode="portal" projectId={uuid} /> in a minimal
 *      portal frame (no AppShell — the portal isn't part of the PM client)
 *   6. Render <UploadSection> below ProjectPage for client→PM photo flow
 *      (the one feature the unified component doesn't reproduce per memory:
 *      feedback_photos_one_directional_from_pm.md)
 */
export function Portal() {
  const { slug = '' } = useParams<{ slug: string }>();
  const qc = useQueryClient();
  // D-124 — tab state lives in the URL so clients can bookmark the photos
  // view directly (?tab=photos). D-130 adds `my-photos` for the
  // client-side album section. Default tab is `overview`; any unknown
  // value normalizes to overview.
  const [searchParams, setSearchParams] = useSearchParams();
  const rawTab = searchParams.get('tab');
  const tab: PortalTab =
    rawTab === 'photos' ? 'photos' : rawTab === 'my-photos' ? 'my-photos' : 'overview';
  const setTab = (next: PortalTab) => {
    const sp = new URLSearchParams(searchParams);
    if (next === 'overview') sp.delete('tab');
    else sp.set('tab', next);
    setSearchParams(sp, { replace: false });
  };
  const [bootState, setBootState] = useState<
    | { kind: 'loading' }
    | { kind: 'needs_pin' }
    | { kind: 'not_found' }
    | { kind: 'error'; message: string }
    | {
        kind: 'ready';
        projectId: string;
        project: PortalProject;
        phases: PortalPhase[];
      }
  >({ kind: 'loading' });

  // Track when the queries are wired so we only re-run if slug changes.
  // The bootstrap intentionally bypasses TanStack Query for the parallel
  // initial fetch — we control the timing precisely so the cache is full
  // before ProjectPage mounts and its useQuery hooks run.
  useEffect(() => {
    if (!slug) return;
    let cancelled = false;

    (async () => {
      try {
        const projectResp = await getProject(slug);
        if (cancelled) return;

        const { project, phases } = projectResp;
        // Fire the rest in parallel — they all need a valid session cookie
        // which the project fetch above already validated.
        const [activityResp, tasksResp, permitsResp] = await Promise.all([
          getActivity(slug),
          getTasks(slug),
          getPermits(slug),
        ]);
        if (cancelled) return;

        // ---------- Map D1 shapes → SQLite shapes ----------
        const projectDetail = mapProjectDetail(project, slug);
        const phaseSummaries = phases.map(mapPhaseSummary);
        const tasksByPhase = new Map<string, Task[]>();
        for (const t of tasksResp.tasks) {
          const list = tasksByPhase.get(t.phase_id) ?? [];
          list.push(mapTask(t, project.id));
          tasksByPhase.set(t.phase_id, list);
        }
        const activityEntries = activityResp.activity.map((e) =>
          mapActivityEntry(e, project.id),
        );

        // ---------- Pre-fill the TanStack Query caches ----------
        // ProjectPage's top-level useQuery: ['projects', 'one', projectId]
        const projectDetailResponse: ProjectDetailResponse = {
          project: projectDetail,
          phases: phaseSummaries,
          counts: synthesizeCounts(
            activityResp.activity.length,
            tasksResp.tasks.length,
            permitsResp.permits.length,
            project.pm_internal_counts_json,
          ),
        };
        qc.setQueryDefaults(['projects', 'one', project.id], {
          staleTime: Infinity,
          gcTime: Infinity,
        });
        qc.setQueryData<ProjectDetailResponse>(
          ['projects', 'one', project.id],
          projectDetailResponse,
        );

        // ClientUpdatesHeadlines: ['activity-log', projectId, 'published']
        const publishedFeed: ActivityLogListResponse = {
          entries: activityEntries,
          count: activityEntries.length,
        };
        qc.setQueryDefaults(['activity-log', project.id, 'published'], {
          staleTime: Infinity,
          gcTime: Infinity,
        });
        qc.setQueryData<ActivityLogListResponse>(
          ['activity-log', project.id, 'published'],
          publishedFeed,
        );

        // Per-phase task list: ['tasks', 'phase', phaseId]
        // Per-phase activity (portal-filtered):
        //   ['activity-log', projectId, 'phase', phaseId, 'published']
        for (const phase of phaseSummaries) {
          const phaseTasks = tasksByPhase.get(phase.id) ?? [];
          const tasksResponse: TasksListResponse = {
            tasks: phaseTasks,
            count: phaseTasks.length,
          };
          qc.setQueryDefaults(['tasks', 'phase', phase.id], {
            staleTime: Infinity,
            gcTime: Infinity,
          });
          qc.setQueryData<TasksListResponse>(
            ['tasks', 'phase', phase.id],
            tasksResponse,
          );

          const phaseEntries = activityEntries.filter(
            (e) => e.phase_id === phase.id,
          );
          const phaseActivity: ActivityLogListResponse = {
            entries: phaseEntries,
            count: phaseEntries.length,
          };
          qc.setQueryDefaults(
            ['activity-log', project.id, 'phase', phase.id, 'published'],
            { staleTime: Infinity, gcTime: Infinity },
          );
          qc.setQueryData<ActivityLogListResponse>(
            ['activity-log', project.id, 'phase', phase.id, 'published'],
            phaseActivity,
          );
        }

        setBootState({
          kind: 'ready',
          projectId: project.id,
          project,
          phases,
        });
      } catch (err) {
        if (cancelled) return;
        if (err instanceof PortalApiError) {
          if (err.requirePin) {
            setBootState({ kind: 'needs_pin' });
            return;
          }
          if (err.status === 404) {
            setBootState({ kind: 'not_found' });
            return;
          }
        }
        setBootState({
          kind: 'error',
          message: err instanceof Error ? err.message : 'unknown error',
        });
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [slug, qc]);

  // D-129.1 — phases no longer threaded to PortalPhotosTab; the album
  // header dropped phase chips entirely. (Phases are still pre-filled
  // into the TanStack cache for the Overview ProjectPage render.)

  // ------- 401 — PIN gate -------
  if (bootState.kind === 'needs_pin') {
    return (
      <PinGate
        slug={slug}
        onUnlocked={() => {
          // Re-trigger the bootstrap effect by flipping back to loading,
          // which causes the effect to re-run on the next render.
          setBootState({ kind: 'loading' });
        }}
      />
    );
  }

  // ------- 404 — project not found -------
  if (bootState.kind === 'not_found') {
    return (
      <CenteredMessage title="Project not found">
        We couldn&apos;t find a project at this URL. Double-check the link
        your project manager sent.
      </CenteredMessage>
    );
  }

  // ------- Other errors -------
  if (bootState.kind === 'error') {
    return (
      <CenteredMessage title="Something went wrong">
        We couldn&apos;t load this project. Please try again in a moment.
        <span className="block mt-2 text-xs text-[var(--color-ink-3)]">
          {bootState.message}
        </span>
      </CenteredMessage>
    );
  }

  // ------- Loading -------
  if (bootState.kind === 'loading') {
    return (
      <CenteredMessage title="Loading…">
        Pulling the latest project state.
      </CenteredMessage>
    );
  }

  // ------- Ready: mount the unified ProjectPage -------
  //
  // D-123 — outer wrapper carries the same gray-palette CSS vars
  // ProjectPage scopes on its own root, so the entire portal page reads
  // as one continuous gray surface.
  //
  // D-124 — page is now tabbed: Overview (default) renders ProjectPage,
  // Photos renders the PM-pushed project-photos gallery + the existing
  // client UploadSection (per C 2026-05-11: photos belong on their own
  // tab, NOT on the main page). The tab bar lives BELOW ProjectPage's
  // built-in project header so the project name/subtitle remains the
  // anchor at the top of every view, and the tabs slot in like
  // navigation under it.
  //
  // The tab strip is rendered as a fixed-position sibling above the
  // ProjectPage subtree because ProjectPage owns its own header (project
  // name + subtitle + project color bar) at the top of its return. We
  // mount the tabs BETWEEN the color bar (which renders first as part of
  // ProjectPage) and the content. Easiest: render the project color bar
  // + name header ourselves up front, render tab strip, then mount
  // ProjectPage with a `chromeless` mode — but that's a bigger refactor.
  // Cleaner short-term: render tabs as a horizontal strip at the very
  // top of the portal-only wrapper, before ProjectPage. Visually they
  // sit above the project name/header band. Re-evaluate during the next
  // visual polish pass if it reads better below.
  return (
    <div
      className="min-h-screen flex flex-col"
      style={{
        ['--color-page-bg' as string]: '#F3F4F6',
        ['--color-card' as string]: '#FFFFFF',
        ['--color-section-header' as string]: '#D1D5DB',
        ['--color-border' as string]: '#D1D5DB',
        ['--color-border-strong' as string]: '#9CA3AF',
        ['--color-surface' as string]: '#F3F4F6',
        ['--color-surface-2' as string]: '#E5E7EB',
        background: '#F3F4F6',
      }}
    >
      <PortalTabBar tab={tab} setTab={setTab} />

      {tab === 'overview' && (
        <ProjectPage viewMode="portal" projectId={bootState.projectId} />
      )}

      {tab === 'photos' && (
        <PortalPhotosTab slug={slug} />
      )}

      {tab === 'my-photos' && (
        <ClientPhotosTab slug={slug} />
      )}

      <footer className="max-w-6xl mx-auto px-8 mt-auto pb-6 text-xs text-ink-3 text-center">
        Rough-In Advisory LLC
      </footer>
    </div>
  );
}

// ============================================================
// PortalTabBar — Overview / Photos switch (D-124)
// ============================================================
function PortalTabBar({
  tab,
  setTab,
}: {
  tab: PortalTab;
  setTab: (next: PortalTab) => void;
}) {
  // D-125 — branded navy band per C's direction: "If your going with a
  // top bar in client section, make it or blue or something. Add
  // Rough_In Advisory brand on it somewhere." Navy matches the PM-side
  // AppShell topbar (same `--color-navy` token in the portal theme),
  // so the PM-side and client-side chrome feel like one product.
  // Orange accent under the active tab carries the Conduit/Rough-In
  // brand color forward.
  return (
    <div
      style={{
        background: 'var(--color-navy)',
        borderBottom: '1px solid var(--color-navy-line)',
      }}
    >
      <div className="max-w-6xl mx-auto px-8 flex items-center justify-between gap-6 h-14">
        {/* Wordmark — left side. Plain text mark (no logo SVG yet),
            uppercase + tight letter-spacing so it reads as a brand
            band element rather than running copy. Tagline below for
            authority. */}
        <div className="flex flex-col leading-tight">
          <span
            className="text-white font-bold tracking-wide"
            style={{ fontSize: '15px', letterSpacing: '0.04em' }}
          >
            ROUGH-IN ADVISORY
          </span>
          <span
            className="text-[10px] tracking-wider uppercase"
            style={{
              color: 'rgba(255,255,255,0.55)',
              letterSpacing: '0.10em',
            }}
          >
            Client portal
          </span>
        </div>

        {/* Tabs — right side */}
        <nav
          className="flex items-center gap-0 h-full"
          role="tablist"
          aria-label="Portal sections"
        >
          <PortalTab
            label="Overview"
            active={tab === 'overview'}
            onClick={() => setTab('overview')}
          />
          <PortalTab
            label="PM Photos"
            active={tab === 'photos'}
            onClick={() => setTab('photos')}
          />
          <PortalTab
            label="Client Photos"
            active={tab === 'my-photos'}
            onClick={() => setTab('my-photos')}
          />
        </nav>
      </div>
    </div>
  );
}

function PortalTab({
  label,
  active,
  onClick,
}: {
  label: string;
  active: boolean;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      role="tab"
      aria-selected={active}
      onClick={onClick}
      className="relative px-5 h-full text-sm font-medium transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-accent"
      style={{
        color: active ? '#FFFFFF' : 'rgba(255,255,255,0.65)',
      }}
    >
      {label}
      {active && (
        <span
          aria-hidden
          className="absolute left-3 right-3 bottom-0 h-[3px]"
          style={{ background: 'var(--color-accent)' }}
        />
      )}
    </button>
  );
}

// ============================================================
// PortalPhotosTab — two-section photo surface (D-124)
//
// Section 1: "Project photos" — PM-pushed, READ-ONLY for clients.
//   Placeholder copy in Stage A; populated by Stage B's publish
//   pipeline (PM marks photo client-visible on the management UI →
//   cloudPublisher pushes bytes to R2 + metadata to D1 → portal renders
//   here). Empty-state placeholder explains the flow to clients
//   without exposing PM-internal details.
//
// Section 2: "Share progress photos" — client→PM upload via the
//   existing UploadSection (writes to R2 + D1 client_uploaded_photos;
//   PM pulls down via cloudStoragePuller). Per memory
//   feedback_photos_one_directional_from_pm.md the two flows are
//   strictly separate: client uploads do NOT feed the project-photos
//   render above; they land in the PM-side from-clients triage.
// ============================================================
function PortalPhotosTab({
  slug,
}: {
  slug: string;
}) {
  // D-128 — album-centric gallery. Albums are the primary grouping;
  // photos live under their parent album. Album header carries the
  // identity (name + optional phase chip); per-photo metadata is NOT
  // surfaced.
  const photosQ = useQuery({
    queryKey: ['portal', slug, 'photos'],
    queryFn: () => getProjectPhotos(slug),
    enabled: !!slug,
  });
  const albumsQ = useQuery({
    queryKey: ['portal', slug, 'albums'],
    queryFn: () => getProjectAlbums(slug),
    enabled: !!slug,
  });

  // Lightbox state — index into the flat photo list (in render order
  // across all albums) so ← / → traverses through everything sequentially.
  const [lightboxIndex, setLightboxIndex] = useState<number | null>(null);

  // D-128 — group photos under their parent album. Albums in
  // sort_order ASC, created_at DESC (newest album at top per Worker
  // SELECT order, which we trust). Photos within an album sorted by
  // uploaded_at DESC. Flat list (`flatPhotos`) carries the across-
  // album traversal order for the lightbox.
  // D-129.1 — phasesById lookup dropped (phase chip on album header
  // was cut along with the phase attachment feature).
  const { groupedAlbums, flatPhotos } = useMemo(() => {
    const albums = albumsQ.data?.albums ?? [];
    const allPhotos = photosQ.data?.photos ?? [];
    const photosByAlbum = new Map<string, PortalPhoto[]>();
    for (const p of allPhotos) {
      if (!p.album_id) continue;
      const list = photosByAlbum.get(p.album_id) ?? [];
      list.push(p);
      photosByAlbum.set(p.album_id, list);
    }
    // Sort photos within each album by uploaded_at DESC.
    for (const list of photosByAlbum.values()) {
      list.sort((a, b) => b.uploaded_at.localeCompare(a.uploaded_at));
    }
    const grouped: Array<{ album: PortalAlbum; photos: PortalPhoto[] }> = [];
    const flat: PortalPhoto[] = [];
    for (const album of albums) {
      const photos = photosByAlbum.get(album.id) ?? [];
      grouped.push({ album, photos });
      for (const p of photos) flat.push(p);
    }
    return { groupedAlbums: grouped, flatPhotos: flat };
  }, [albumsQ.data, photosQ.data]);

  const isLoading = photosQ.isLoading || albumsQ.isLoading;
  const isError = photosQ.isError || albumsQ.isError;
  const isEmpty = groupedAlbums.every((g) => g.photos.length === 0);

  return (
    <div className="max-w-6xl mx-auto w-full px-8 py-6 space-y-8">
      <section>
        {isLoading ? (
          <p className="text-sm text-ink-3 italic">Loading photos…</p>
        ) : isError ? (
          <p
            className="text-sm"
            style={{ color: 'var(--color-danger)' }}
          >
            Couldn't load project photos.
          </p>
        ) : groupedAlbums.length === 0 || isEmpty ? (
          <div
            className="rounded-md border px-5 py-6 text-sm"
            style={{
              background: 'var(--color-card)',
              borderColor: 'var(--color-border)',
              color: 'var(--color-ink-2)',
            }}
          >
            <p>
              Photo albums shared by your project manager will appear here. Until
              then, you can share your own progress photos using the section
              below — they'll land directly with the PM for review.
            </p>
          </div>
        ) : (
          <div className="space-y-8">
            {groupedAlbums.map((group) => {
              if (group.photos.length === 0) return null;
              // Find this album's first photo's index in flatPhotos for
              // lightbox starting position.
              const firstIdx = flatPhotos.findIndex(
                (p) => p.album_id === group.album.id,
              );
              return (
                <PortalAlbumSection
                  key={group.album.id}
                  album={group.album}
                  photos={group.photos}
                  slug={slug}
                  onPhotoClick={(photo) => {
                    const idx = flatPhotos.findIndex((p) => p.id === photo.id);
                    setLightboxIndex(idx >= 0 ? idx : firstIdx);
                  }}
                />
              );
            })}
          </div>
        )}
      </section>

      {/* D-129 — Client upload section temporarily REMOVED from the
          PM-photos Photos tab. The legacy UploadSection (D-086/D-087)
          mixed client→PM uploads into the same surface as PM-pushed
          albums, which was visually confusing per C's feedback. The
          dedicated "Client photo albums" surface is queued as D-130:
          its own area, same album-centric UX, separate data path
          (client_uploaded_albums on D1 + R2 keys under
          client_uploads/<project>/<album>/<photo>). Until D-130 ships,
          this tab is PM-photos-only. */}

      {lightboxIndex !== null && (
        <PhotoLightbox
          photos={flatPhotos}
          index={lightboxIndex}
          slug={slug}
          onClose={() => setLightboxIndex(null)}
          onIndexChange={(next) => setLightboxIndex(next)}
        />
      )}
    </div>
  );
}

// ============================================================
// PortalAlbumSection — name header + bare photo grid (D-128)
// ============================================================
function PortalAlbumSection({
  album,
  photos,
  slug,
  onPhotoClick,
}: {
  album: PortalAlbum;
  photos: PortalPhoto[];
  slug: string;
  onPhotoClick: (photo: PortalPhoto) => void;
}) {
  // D-129.1 — phase + sub_category chips dropped entirely. Per C:
  // "just drop that function entirely. I'm not going to use it, its
  // going to clutter that flow up." Album header now shows name + date
  // only. PortalAlbum's phase_id and sub_category fields stay on the
  // schema for back-compat but aren't surfaced.
  return (
    <div>
      <header className="mb-3 border-b border-border pb-1.5">
        <h3 className="text-xl font-bold text-ink leading-tight">{album.name}</h3>
        {album.event_date && (
          <p className="mt-1 text-[11px] num font-semibold"
             style={{ color: 'var(--color-ink-2)' }}>
            {formatAlbumEventDate(album.event_date)}
          </p>
        )}
      </header>
      <ul className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-2">
        {photos.map((p) => (
          <li key={p.id}>
            <PortalPhotoTile
              photo={p}
              slug={slug}
              onClick={() => onPhotoClick(p)}
            />
          </li>
        ))}
      </ul>
    </div>
  );
}

// ============================================================
// PortalPhotoTile — bare thumbnail, no per-photo metadata (D-128)
// ============================================================
function PortalPhotoTile({
  photo,
  slug,
  onClick,
}: {
  photo: PortalPhoto;
  slug: string;
  onClick: () => void;
}) {
  const rawUrl = portalPhotoRawUrl(slug, photo.id);
  const alt = photo.portal_title || photo.document_name || 'Photo';
  return (
    <button
      type="button"
      onClick={onClick}
      className="w-full block rounded-md border overflow-hidden hover:shadow-md transition-shadow focus:outline-none focus:ring-2 focus:ring-accent"
      style={{
        background: 'var(--color-card)',
        borderColor: 'var(--color-border)',
        aspectRatio: '4 / 3',
      }}
      title={alt}
    >
      <img
        src={rawUrl}
        alt={alt}
        className="w-full h-full object-cover"
        loading="lazy"
      />
    </button>
  );
}

// D-128 — `PortalPhotoCard` removed. Album-centric model puts metadata
// (name + optional phase) on the album header, NOT per-tile.
// PortalPhotoTile (defined inside PortalPhotosTab above) is the bare
// thumbnail used in the gallery.

// ============================================================
// PhotoLightbox — full-screen viewer with prev/next (D-125)
// ============================================================
function PhotoLightbox({
  photos,
  index,
  slug,
  onClose,
  onIndexChange,
}: {
  photos: PortalPhoto[];
  index: number;
  slug: string;
  onClose: () => void;
  onIndexChange: (next: number) => void;
}) {
  const photo = photos[index];

  // Keyboard nav: Esc closes, ← / → step through.
  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') onClose();
      else if (e.key === 'ArrowLeft' && index > 0) onIndexChange(index - 1);
      else if (e.key === 'ArrowRight' && index < photos.length - 1)
        onIndexChange(index + 1);
    }
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [index, photos.length, onClose, onIndexChange]);

  if (!photo) return null;

  const alt = photo.portal_title || photo.document_name || 'Photo';
  const rawUrl = portalPhotoRawUrl(slug, photo.id);
  const hasPrev = index > 0;
  const hasNext = index < photos.length - 1;

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label={`Photo ${index + 1} of ${photos.length}`}
      className="fixed inset-0 z-50 flex items-center justify-center"
      style={{ background: 'rgba(15, 23, 32, 0.92)' }}
      onClick={onClose}
    >
      {/* Close button — top-right */}
      <button
        type="button"
        onClick={(e) => {
          e.stopPropagation();
          onClose();
        }}
        aria-label="Close lightbox (Esc)"
        className="absolute top-4 right-4 w-10 h-10 rounded-full flex items-center justify-center text-white text-2xl leading-none focus:outline-none focus:ring-2 focus:ring-white/50"
        style={{ background: 'rgba(255,255,255,0.10)' }}
        title="Close (Esc)"
      >
        ×
      </button>

      {/* D-128 — counter only; no title or date per the album-centric
          "no metadata per photo" rule. The album name lives on the
          gallery section above, not here. */}
      <div className="absolute top-4 left-4">
        <p className="text-[11px] uppercase tracking-wider text-white/60 num">
          {index + 1} / {photos.length}
        </p>
      </div>

      {/* Prev */}
      {hasPrev && (
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            onIndexChange(index - 1);
          }}
          aria-label="Previous photo (←)"
          className="absolute left-4 top-1/2 -translate-y-1/2 w-12 h-12 rounded-full flex items-center justify-center text-white text-3xl leading-none focus:outline-none focus:ring-2 focus:ring-white/50"
          style={{ background: 'rgba(255,255,255,0.10)' }}
          title="Previous (←)"
        >
          ‹
        </button>
      )}

      {/* Next */}
      {hasNext && (
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            onIndexChange(index + 1);
          }}
          aria-label="Next photo (→)"
          className="absolute right-4 top-1/2 -translate-y-1/2 w-12 h-12 rounded-full flex items-center justify-center text-white text-3xl leading-none focus:outline-none focus:ring-2 focus:ring-white/50"
          style={{ background: 'rgba(255,255,255,0.10)' }}
          title="Next (→)"
        >
          ›
        </button>
      )}

      {/* Image — stopPropagation so clicking the image doesn't close */}
      <img
        src={rawUrl}
        alt={alt}
        onClick={(e) => e.stopPropagation()}
        className="max-w-[92vw] max-h-[88vh] object-contain"
        style={{ boxShadow: '0 20px 60px rgba(0,0,0,0.5)' }}
      />
    </div>
  );
}

// D-128 — `formatPortalDate` removed (was used by the deleted
// PortalPhotoCard; album-centric render doesn't surface per-photo dates).

/** D-129 — format an album event_date (YYYY-MM-DD) for the portal header.
 *  Renders as e.g. "May 8, 2026". UTC-anchored to dodge timezone shifts
 *  on the date-only input. */
function formatAlbumEventDate(yyyyMmDd: string): string {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(yyyyMmDd);
  if (!m) return yyyyMmDd;
  const d = new Date(Date.UTC(
    Number(m[1]),
    Number(m[2]) - 1,
    Number(m[3]),
  ));
  if (Number.isNaN(d.getTime())) return yyyyMmDd;
  return d.toLocaleDateString('en-US', {
    month: 'long',
    day: 'numeric',
    year: 'numeric',
    timeZone: 'UTC',
  });
}

// ============================================================
// D-130 — ClientPhotosTab: client-side album CRUD on the portal
// ============================================================
//
// Three things go here:
//   1. NewClientAlbumCard — all-in-one create form (name + date + drop
//      zone in one panel). Per C's friction-fix direction: client
//      doesn't see a separate "create album" → "now upload" two-step.
//      They expand the form, fill in name + date + drop photos, hit
//      "Create & upload" once.
//   2. ClientAlbumSection — each existing album: header (name, date,
//      count, edit, delete) + drop zone for more photos + photo grid +
//      lightbox.
//   3. ClientPhotosTab — the tab root: query both lists, group photos
//      under their album.
// ============================================================

function ClientPhotosTab({ slug }: { slug: string }) {
  const qc = useQueryClient();
  const albumsQ = useQuery({
    queryKey: ['portal', slug, 'client-albums'],
    queryFn: () => listClientAlbums(slug),
    enabled: !!slug,
  });
  const photosQ = useQuery({
    queryKey: ['portal', slug, 'client-photos'],
    queryFn: () => listClientPhotos(slug),
    enabled: !!slug,
  });
  const [lightboxIndex, setLightboxIndex] = useState<number | null>(null);

  const invalidate = useCallback(() => {
    qc.invalidateQueries({ queryKey: ['portal', slug, 'client-albums'] });
    qc.invalidateQueries({ queryKey: ['portal', slug, 'client-photos'] });
  }, [qc, slug]);

  const { groupedAlbums, flatPhotos } = useMemo(() => {
    const albums = albumsQ.data?.albums ?? [];
    const photos = photosQ.data?.photos ?? [];
    const byAlbum = new Map<string, ClientPhoto[]>();
    for (const p of photos) {
      if (!p.album_id) continue;
      const list = byAlbum.get(p.album_id) ?? [];
      list.push(p);
      byAlbum.set(p.album_id, list);
    }
    for (const list of byAlbum.values()) {
      list.sort((a, b) => b.uploaded_at.localeCompare(a.uploaded_at));
    }
    const grouped: Array<{ album: ClientAlbum; photos: ClientPhoto[] }> = [];
    const flat: ClientPhoto[] = [];
    for (const album of albums) {
      const list = byAlbum.get(album.id) ?? [];
      grouped.push({ album, photos: list });
      for (const p of list) flat.push(p);
    }
    return { groupedAlbums: grouped, flatPhotos: flat };
  }, [albumsQ.data, photosQ.data]);

  const hasAlbums = (albumsQ.data?.albums ?? []).length > 0;

  return (
    <div className="max-w-6xl mx-auto w-full px-8 py-6 space-y-6">
      <section>
        <h2 className="text-xs font-semibold tracking-wide uppercase text-ink-2 mb-1">
          Your photo albums
        </h2>
        <p className="text-[12px] text-ink-3 mb-3 leading-snug">
          Share photos with your project manager. Group them into albums by
          date or topic — site visits, weekly walks, anything you want them
          to see.
        </p>
      </section>

      {/* D-130.1 — Form starts EXPANDED when there are no albums yet, so
          first-time clients see the name/date/drop-zone immediately. Once
          they have one or more albums, it collapses back to a "+ New album"
          pill that ALSO accepts drag-drop (auto-expands + stages). */}
      <NewClientAlbumCard
        slug={slug}
        onCreated={invalidate}
        initialOpen={!hasAlbums}
      />

      {albumsQ.isLoading || photosQ.isLoading ? (
        <p className="text-sm text-ink-3 italic">Loading…</p>
      ) : albumsQ.isError || photosQ.isError ? (
        <p className="text-sm" style={{ color: 'var(--color-danger)' }}>
          Couldn't load your albums.
        </p>
      ) : groupedAlbums.length === 0 ? null /* form is already shown above */ : (
        <div className="space-y-6">
          {groupedAlbums.map((g) => (
            <ClientAlbumSection
              key={g.album.id}
              slug={slug}
              album={g.album}
              photos={g.photos}
              onChanged={invalidate}
              onPhotoClick={(photo) => {
                const idx = flatPhotos.findIndex((p) => p.id === photo.id);
                setLightboxIndex(idx);
              }}
            />
          ))}
        </div>
      )}

      {lightboxIndex !== null && (
        <ClientPhotoLightbox
          photos={flatPhotos}
          index={lightboxIndex}
          slug={slug}
          onClose={() => setLightboxIndex(null)}
          onIndexChange={setLightboxIndex}
        />
      )}
    </div>
  );
}

function NewClientAlbumCard({
  slug,
  onCreated,
  initialOpen = false,
}: {
  slug: string;
  onCreated: () => void;
  /** D-130.1 — when true, form is expanded on mount. Used when the
   *  client has no albums yet, so the empty state shows the form
   *  ready to fill rather than a collapsed pill they have to click. */
  initialOpen?: boolean;
}) {
  const [open, setOpen] = useState(initialOpen);
  const [name, setName] = useState('');
  const [eventDate, setEventDate] = useState('');
  const [stagedFiles, setStagedFiles] = useState<File[]>([]);
  // D-130.3 — track whether the album row has already been created on
  // this submit cycle. If a partial-failure retry happens, we re-attempt
  // only the failed photo uploads instead of POSTing a duplicate album.
  // Cleared on a clean success or full Cancel/reset.
  const [createdAlbumId, setCreatedAlbumId] = useState<string | null>(null);
  const [status, setStatus] = useState<
    | { kind: 'idle' }
    | { kind: 'creating' }
    | { kind: 'uploading'; done: number; total: number }
    | { kind: 'error'; messages: string[] }
  >({ kind: 'idle' });
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [collapsedDragOver, setCollapsedDragOver] = useState(false);

  const reset = () => {
    setOpen(initialOpen); // respect initialOpen on reset
    setName('');
    setEventDate('');
    setStagedFiles([]);
    setCreatedAlbumId(null);
    setStatus({ kind: 'idle' });
  };

  const submit = async () => {
    const trimmedName = name.trim();
    if (!trimmedName) {
      setStatus({ kind: 'error', messages: ['Album name is required.'] });
      return;
    }

    // D-130.3 — if a previous submit already created the album row,
    // skip the POST and reuse the existing id. The retry only attempts
    // photo uploads that didn't make it last time.
    let albumId = createdAlbumId;
    if (!albumId) {
      setStatus({ kind: 'creating' });
      try {
        const resp = await createClientAlbum(slug, {
          name: trimmedName,
          event_date: eventDate || null,
        });
        albumId = resp.album.id;
        setCreatedAlbumId(albumId);
        onCreated(); // invalidate caches so the empty album appears immediately
      } catch (err) {
        setStatus({
          kind: 'error',
          messages: [`Could not create album: ${err instanceof Error ? err.message : 'unknown error'}`],
        });
        return;
      }
    }

    if (stagedFiles.length === 0) {
      // Album-only create (no photos staged). Done.
      onCreated();
      reset();
      return;
    }

    setStatus({ kind: 'uploading', done: 0, total: stagedFiles.length });
    let done = 0;
    const succeededFiles: File[] = [];
    const failures: string[] = [];
    for (const file of stagedFiles) {
      try {
        await uploadPortalPhoto(slug, file, { albumId });
        done += 1;
        succeededFiles.push(file);
        setStatus({ kind: 'uploading', done, total: stagedFiles.length });
      } catch (err) {
        failures.push(
          `${file.name}: ${err instanceof Error ? err.message : 'upload failed'}`,
        );
      }
    }
    onCreated();
    if (failures.length === 0) {
      reset();
    } else {
      // D-130.3 — drop the successfully-uploaded files from staging so
      // a retry click only re-attempts the failed ones. Keep
      // createdAlbumId so retry reuses the same album row.
      setStagedFiles((prev) => prev.filter((f) => !succeededFiles.includes(f)));
      setStatus({ kind: 'error', messages: failures });
    }
  };

  if (!open) {
    // D-130.1 — collapsed pill also accepts drag-drop: opens the form
    // AND stages the dropped files. Solves the "I tried to upload but
    // nothing happened" confusion where users dropped on the closed
    // button.
    return (
      <button
        type="button"
        onClick={() => setOpen(true)}
        onDragOver={(e) => {
          e.preventDefault();
          setCollapsedDragOver(true);
        }}
        onDragLeave={() => setCollapsedDragOver(false)}
        onDrop={(e) => {
          e.preventDefault();
          setCollapsedDragOver(false);
          const files = e.dataTransfer.files;
          setOpen(true);
          if (files && files.length > 0) {
            setStagedFiles((prev) => [...prev, ...Array.from(files)]);
          }
        }}
        className="w-full rounded-md border-2 border-dashed px-4 py-3 text-sm font-medium hover:border-border-strong hover:bg-[var(--color-surface-2)] focus:outline-none focus:ring-2 focus:ring-accent transition-colors"
        style={{
          borderColor: collapsedDragOver
            ? 'var(--color-accent)'
            : 'var(--color-border-strong)',
          background: collapsedDragOver
            ? 'var(--color-warning-soft)'
            : 'transparent',
          color: 'var(--color-accent)',
        }}
      >
        {collapsedDragOver ? 'Drop to start a new album' : '+ New album'}
      </button>
    );
  }

  const busy = status.kind === 'creating' || status.kind === 'uploading';

  return (
    <form
      onSubmit={(e) => {
        e.preventDefault();
        if (!busy) submit();
      }}
      className="rounded-md border p-4 space-y-3"
      style={{ background: 'var(--color-card)', borderColor: 'var(--color-border)' }}
    >
      <h3 className="text-xs font-semibold tracking-wide uppercase text-ink-2">
        New album
      </h3>
      <div className="grid grid-cols-1 md:grid-cols-[2fr,1fr] gap-3">
        <label className="flex flex-col gap-1">
          <span className="text-[11px] uppercase tracking-wider text-ink-3 font-semibold">
            Album name <span className="text-[var(--color-danger)]">*</span>
          </span>
          <input
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="e.g. Site walk — Tuesday"
            autoFocus
            disabled={busy}
            className="text-sm border border-border rounded px-2 py-1.5 bg-white focus:outline-none focus:ring-2 focus:ring-accent"
          />
        </label>
        <label className="flex flex-col gap-1">
          <span className="text-[11px] uppercase tracking-wider text-ink-3 font-semibold">
            Date (optional)
          </span>
          <input
            type="date"
            value={eventDate}
            onChange={(e) => setEventDate(e.target.value)}
            disabled={busy}
            className="text-sm border border-border rounded px-2 py-1.5 bg-white focus:outline-none focus:ring-2 focus:ring-accent"
          />
        </label>
      </div>

      {/* D-130.2 — staged thumbnails grid + drop zone below. Photos are
          visible as small previews with an X to remove; drop zone stays
          available for adding more. Solves the "only 1 photo stages"
          confusion by making it obvious you can keep adding. */}
      {stagedFiles.length > 0 && (
        <div className="space-y-1.5">
          <p className="text-[11px] uppercase tracking-wider text-ink-3 font-semibold">
            {stagedFiles.length} photo{stagedFiles.length === 1 ? '' : 's'} staged
          </p>
          <ul className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-6 gap-1.5">
            {stagedFiles.map((file, idx) => (
              <StagedThumb
                key={`${file.name}-${idx}`}
                file={file}
                onRemove={() => {
                  setStagedFiles((prev) => prev.filter((_, i) => i !== idx));
                }}
                disabled={busy}
              />
            ))}
          </ul>
        </div>
      )}

      {/* D-130.4 — use <label> instead of <div onClick={inputRef.current.click()}>.
          The previous pattern triggered the parent's onClick twice (programmatic
          click on the nested input bubbled back up), which broke click-to-pick
          on some browsers while drag-drop kept working. A label nests the input
          and activates it natively on click — no JS click(), no bubble loop. */}
      <label
        onDragOver={(e) => {
          if (busy) return;
          e.preventDefault();
        }}
        onDrop={(e) => {
          if (busy) return;
          e.preventDefault();
          if (e.dataTransfer.files) {
            setStagedFiles((prev) => [...prev, ...Array.from(e.dataTransfer.files)]);
          }
        }}
        className="block rounded-md border-2 border-dashed py-5 px-4 text-center cursor-pointer text-sm"
        style={{
          borderColor: 'var(--color-border-strong)',
          background: 'var(--color-surface-2)',
          color: 'var(--color-ink-2)',
          opacity: busy ? 0.6 : 1,
        }}
      >
        {stagedFiles.length === 0
          ? 'Drop photos here or click to pick'
          : '+ Add more photos (drop here or click)'}
        <p className="mt-1 text-xs text-ink-3">
          Multi-select in the picker with Ctrl/Cmd+click, or drop multiple at once
        </p>
        <input
          ref={fileInputRef}
          type="file"
          accept="image/*"
          multiple
          disabled={busy}
          onChange={(e) => {
            if (e.target.files) {
              setStagedFiles((prev) => [...prev, ...Array.from(e.target.files!)]);
            }
            if (fileInputRef.current) fileInputRef.current.value = '';
          }}
          className="hidden"
        />
      </label>

      {status.kind === 'creating' && (
        <p className="text-xs text-ink-2 italic">Creating album…</p>
      )}
      {status.kind === 'uploading' && (
        <p className="text-xs text-ink-2 italic">
          Uploading {status.done} of {status.total}…
        </p>
      )}
      {status.kind === 'error' && (
        <ul className="text-xs space-y-0.5" style={{ color: 'var(--color-danger)' }}>
          {status.messages.map((m, i) => (
            <li key={i}>· {m}</li>
          ))}
        </ul>
      )}

      <div className="flex items-center justify-end gap-2 pt-1">
        <button
          type="button"
          onClick={reset}
          disabled={busy}
          className="px-3 py-1.5 rounded text-sm border border-border bg-white hover:bg-[var(--color-surface-2)] text-ink-2 hover:text-ink focus:outline-none focus:ring-2 focus:ring-accent disabled:opacity-50"
        >
          Cancel
        </button>
        <button
          type="submit"
          disabled={busy}
          className="px-3 py-1.5 rounded text-sm font-medium text-white focus:outline-none focus:ring-2 focus:ring-accent disabled:opacity-50"
          style={{ background: 'var(--color-accent)' }}
          title={
            !name.trim()
              ? 'Type an album name first'
              : stagedFiles.length === 0
                ? 'Create the album (you can add photos later)'
                : `Create the album and upload ${stagedFiles.length} photo${stagedFiles.length === 1 ? '' : 's'}`
          }
        >
          {busy
            ? 'Working…'
            : stagedFiles.length === 0
              ? 'Create album'
              : `Create & upload ${stagedFiles.length}`}
        </button>
      </div>
    </form>
  );
}

/** D-130.2 — thumbnail preview of one staged-but-not-yet-uploaded file
 *  in the new-album form. Uses URL.createObjectURL for the preview
 *  (cheap; URL revoked when the file is removed or the component
 *  unmounts). X button removes the file from the staging array. */
function StagedThumb({
  file,
  onRemove,
  disabled,
}: {
  file: File;
  onRemove: () => void;
  disabled: boolean;
}) {
  const [url, setUrl] = useState<string | null>(null);
  useEffect(() => {
    const objectUrl = URL.createObjectURL(file);
    setUrl(objectUrl);
    return () => {
      URL.revokeObjectURL(objectUrl);
    };
  }, [file]);
  return (
    <li className="relative group">
      <div
        className="block w-full aspect-square overflow-hidden rounded border"
        style={{
          background: 'var(--color-section-header)',
          borderColor: 'var(--color-border)',
        }}
        title={file.name}
      >
        {url && (
          <img
            src={url}
            alt={file.name}
            className="w-full h-full object-cover"
          />
        )}
      </div>
      <button
        type="button"
        onClick={onRemove}
        disabled={disabled}
        className="absolute top-1 right-1 w-5 h-5 rounded-full flex items-center justify-center text-xs leading-none opacity-90 hover:opacity-100 focus:outline-none focus:ring-1 focus:ring-white/70 disabled:opacity-30"
        style={{
          background: 'rgba(15, 23, 32, 0.75)',
          color: '#FFFFFF',
        }}
        aria-label={`Remove ${file.name}`}
        title="Remove from staging"
      >
        ×
      </button>
    </li>
  );
}

function ClientAlbumSection({
  slug,
  album,
  photos,
  onChanged,
  onPhotoClick,
}: {
  slug: string;
  album: ClientAlbum;
  photos: ClientPhoto[];
  onChanged: () => void;
  onPhotoClick: (photo: ClientPhoto) => void;
}) {
  const [editing, setEditing] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);

  const renameMut = useMutation({
    mutationFn: (body: { name?: string; event_date?: string | null }) =>
      patchClientAlbum(slug, album.id, body),
    onSuccess: onChanged,
  });
  const deleteMut = useMutation({
    mutationFn: () => deleteClientAlbum(slug, album.id),
    onSuccess: onChanged,
  });

  return (
    <section
      className="rounded-md border overflow-hidden"
      style={{ background: 'var(--color-card)', borderColor: 'var(--color-border)' }}
    >
      <header
        className="flex items-start gap-3 px-4 py-3 border-b"
        style={{ borderColor: 'var(--color-border)' }}
      >
        <div className="flex-1 min-w-0">
          {editing ? (
            <ClientAlbumEditor
              album={album}
              onSave={(body) => {
                renameMut.mutate(body);
                setEditing(false);
              }}
              onCancel={() => setEditing(false)}
            />
          ) : (
            <>
              <h3 className="text-base font-bold text-ink leading-tight truncate">
                {album.name}
              </h3>
              <p className="mt-0.5 text-[11px] num text-ink-3 flex items-center gap-2 flex-wrap">
                {album.event_date && (
                  <span
                    className="font-semibold"
                    style={{ color: 'var(--color-ink-2)' }}
                  >
                    {formatAlbumEventDate(album.event_date)}
                  </span>
                )}
                <span>
                  {photos.length} photo{photos.length === 1 ? '' : 's'}
                </span>
              </p>
            </>
          )}
        </div>
        {!editing && (
          <div className="flex items-center gap-1 flex-shrink-0">
            <button
              type="button"
              onClick={() => setEditing(true)}
              className="text-xs px-2 py-1 rounded text-ink-2 hover:text-ink hover:bg-[var(--color-surface-2)] focus:outline-none focus:ring-1 focus:ring-accent"
            >
              Edit
            </button>
            <button
              type="button"
              onClick={() => setConfirmDelete(true)}
              className="text-xs px-2 py-1 rounded text-ink-3 hover:text-[var(--color-danger)] hover:bg-[var(--color-danger-soft)] focus:outline-none focus:ring-1 focus:ring-accent"
              title="Delete album + all its photos"
            >
              ✕
            </button>
          </div>
        )}
      </header>

      <ClientAlbumUploadZone
        slug={slug}
        albumId={album.id}
        onUploaded={onChanged}
      />

      {photos.length > 0 && (
        <ul className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-2 p-3">
          {photos.map((p) => (
            <ClientPhotoTile
              key={p.id}
              slug={slug}
              photo={p}
              onDeleted={onChanged}
              onClick={() => onPhotoClick(p)}
            />
          ))}
        </ul>
      )}

      {confirmDelete && (
        <div
          role="dialog"
          aria-modal="true"
          className="fixed inset-0 z-50 flex items-center justify-center px-4"
          style={{ background: 'rgba(0,0,0,0.5)' }}
          onClick={() => setConfirmDelete(false)}
        >
          <div
            className="rounded-lg max-w-md w-full p-5 shadow-xl"
            style={{ background: 'var(--color-card)' }}
            onClick={(e) => e.stopPropagation()}
          >
            <h4 className="text-base font-semibold text-ink">
              Delete "{album.name}"?
            </h4>
            <p className="mt-2 text-sm text-ink-2">
              Removes the album and its {photos.length} photo
              {photos.length === 1 ? '' : 's'}. This can't be undone.
            </p>
            <div className="mt-4 flex items-center justify-end gap-2">
              <button
                type="button"
                onClick={() => setConfirmDelete(false)}
                className="px-3 py-1.5 rounded text-sm border border-border bg-white hover:bg-[var(--color-surface-2)] text-ink-2 hover:text-ink focus:outline-none focus:ring-2 focus:ring-accent"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={() => {
                  setConfirmDelete(false);
                  deleteMut.mutate();
                }}
                disabled={deleteMut.isPending}
                className="px-3 py-1.5 rounded text-sm font-medium text-white focus:outline-none focus:ring-2 focus:ring-accent disabled:opacity-50"
                style={{ background: 'var(--color-danger)' }}
              >
                Yes, delete
              </button>
            </div>
          </div>
        </div>
      )}
    </section>
  );
}

function ClientAlbumEditor({
  album,
  onSave,
  onCancel,
}: {
  album: ClientAlbum;
  onSave: (body: { name: string; event_date: string | null }) => void;
  onCancel: () => void;
}) {
  const [name, setName] = useState(album.name);
  const [eventDate, setEventDate] = useState(album.event_date ?? '');
  return (
    <div className="space-y-2">
      <div className="grid grid-cols-[2fr,1fr] gap-2">
        <input
          type="text"
          value={name}
          onChange={(e) => setName(e.target.value)}
          autoFocus
          className="text-base font-semibold border border-border rounded px-2 py-1 bg-white focus:outline-none focus:ring-2 focus:ring-accent"
          onKeyDown={(e) => {
            if (e.key === 'Enter' && name.trim())
              onSave({ name: name.trim(), event_date: eventDate || null });
            else if (e.key === 'Escape') onCancel();
          }}
        />
        <input
          type="date"
          value={eventDate}
          onChange={(e) => setEventDate(e.target.value)}
          className="text-xs border border-border rounded px-2 py-1 bg-white focus:outline-none focus:ring-2 focus:ring-accent"
        />
      </div>
      <div className="flex items-center gap-2">
        <button
          type="button"
          onClick={() => name.trim() && onSave({ name: name.trim(), event_date: eventDate || null })}
          disabled={!name.trim()}
          className="px-2 py-1 rounded text-xs font-medium text-white focus:outline-none focus:ring-2 focus:ring-accent disabled:opacity-50"
          style={{ background: 'var(--color-accent)' }}
        >
          Save
        </button>
        <button
          type="button"
          onClick={onCancel}
          className="px-2 py-1 rounded text-xs border border-border bg-white hover:bg-[var(--color-surface-2)] text-ink-2 hover:text-ink focus:outline-none focus:ring-2 focus:ring-accent"
        >
          Cancel
        </button>
      </div>
    </div>
  );
}

function ClientAlbumUploadZone({
  slug,
  albumId,
  onUploaded,
}: {
  slug: string;
  albumId: string;
  onUploaded: () => void;
}) {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [dragOver, setDragOver] = useState(false);
  const [status, setStatus] = useState<
    | { kind: 'idle' }
    | { kind: 'uploading'; done: number; total: number }
    | { kind: 'error'; messages: string[] }
  >({ kind: 'idle' });

  const handleFiles = async (files: FileList | File[]) => {
    const arr = Array.from(files).filter((f) => f.size > 0);
    if (arr.length === 0) return;
    setStatus({ kind: 'uploading', done: 0, total: arr.length });
    let done = 0;
    const failures: string[] = [];
    for (const file of arr) {
      try {
        await uploadPortalPhoto(slug, file, { albumId });
        done += 1;
        setStatus({ kind: 'uploading', done, total: arr.length });
      } catch (err) {
        failures.push(`${file.name}: ${err instanceof Error ? err.message : 'upload failed'}`);
      }
    }
    if (done > 0) onUploaded();
    setStatus(failures.length > 0 ? { kind: 'error', messages: failures } : { kind: 'idle' });
  };

  return (
    <div className="px-3 pt-3">
      {/* D-130.4 — same <label> pattern as NewClientAlbumCard above:
          previously a <div onClick={fileInputRef.current.click()}> wrapping
          the hidden input, which broke click-to-pick on the second-album
          add-more flow (the synthetic click bubbled back to the parent and
          re-entered .click(), eating the picker). <label> activates the
          nested input natively without bubbling another click event. */}
      <label
        onDragOver={(e) => {
          e.preventDefault();
          setDragOver(true);
        }}
        onDragLeave={() => setDragOver(false)}
        onDrop={(e) => {
          e.preventDefault();
          setDragOver(false);
          if (e.dataTransfer.files) handleFiles(e.dataTransfer.files);
        }}
        className="block rounded-md border-2 border-dashed py-3 px-4 text-center cursor-pointer text-xs"
        style={{
          borderColor: dragOver
            ? 'var(--color-accent)'
            : 'var(--color-border-strong)',
          background: dragOver
            ? 'var(--color-warning-soft)'
            : 'var(--color-surface-2)',
          color: 'var(--color-ink-2)',
        }}
      >
        {status.kind === 'uploading'
          ? `Uploading ${status.done} of ${status.total}…`
          : 'Drop more photos here or click to pick'}
        <input
          ref={fileInputRef}
          type="file"
          accept="image/*"
          multiple
          onChange={(e) => {
            if (e.target.files) handleFiles(e.target.files);
            if (fileInputRef.current) fileInputRef.current.value = '';
          }}
          className="hidden"
        />
      </label>
      {status.kind === 'error' && (
        <ul className="mt-2 text-[11px]" style={{ color: 'var(--color-danger)' }}>
          {status.messages.map((m, i) => (
            <li key={i}>· {m}</li>
          ))}
        </ul>
      )}
    </div>
  );
}

function ClientPhotoTile({
  slug,
  photo,
  onDeleted,
  onClick,
}: {
  slug: string;
  photo: ClientPhoto;
  onDeleted: () => void;
  onClick: () => void;
}) {
  const [confirmDelete, setConfirmDelete] = useState(false);
  const deleteMut = useMutation({
    mutationFn: () => deleteClientPhoto(slug, photo.id),
    onSuccess: onDeleted,
  });
  return (
    <li className="relative group">
      <button
        type="button"
        onClick={onClick}
        className="block w-full aspect-[4/3] overflow-hidden rounded border border-border focus:outline-none focus:ring-2 focus:ring-accent"
        style={{ background: 'var(--color-section-header)' }}
        title="Click to view full size"
      >
        <img
          src={clientPhotoRawUrl(slug, photo.id)}
          alt={photo.uploader_label || 'Photo'}
          loading="lazy"
          className="w-full h-full object-cover"
        />
      </button>
      <button
        type="button"
        onClick={() => setConfirmDelete(true)}
        className="absolute top-1.5 right-1.5 w-7 h-7 rounded-full flex items-center justify-center text-base leading-none opacity-0 group-hover:opacity-100 focus:opacity-100 focus:outline-none focus:ring-2 focus:ring-white/70 transition-opacity"
        style={{
          background: 'rgba(185, 28, 28, 0.92)',
          color: '#FFFFFF',
        }}
        aria-label="Delete photo"
        title="Delete this photo"
      >
        ✕
      </button>

      {confirmDelete && (
        <div
          role="dialog"
          aria-modal="true"
          className="fixed inset-0 z-50 flex items-center justify-center px-4"
          style={{ background: 'rgba(0,0,0,0.5)' }}
          onClick={() => setConfirmDelete(false)}
        >
          <div
            className="rounded-lg max-w-md w-full p-5 shadow-xl"
            style={{ background: 'var(--color-card)' }}
            onClick={(e) => e.stopPropagation()}
          >
            <h4 className="text-base font-semibold text-ink">Delete this photo?</h4>
            <p className="mt-2 text-sm text-ink-2">
              Removes the photo from this album. This can't be undone.
            </p>
            <div className="mt-4 flex items-center justify-end gap-2">
              <button
                type="button"
                onClick={() => setConfirmDelete(false)}
                className="px-3 py-1.5 rounded text-sm border border-border bg-white hover:bg-[var(--color-surface-2)] text-ink-2 hover:text-ink focus:outline-none focus:ring-2 focus:ring-accent"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={() => {
                  setConfirmDelete(false);
                  deleteMut.mutate();
                }}
                disabled={deleteMut.isPending}
                className="px-3 py-1.5 rounded text-sm font-medium text-white focus:outline-none focus:ring-2 focus:ring-accent disabled:opacity-50"
                style={{ background: 'var(--color-danger)' }}
              >
                Yes, delete
              </button>
            </div>
          </div>
        </div>
      )}
    </li>
  );
}

function ClientPhotoLightbox({
  photos,
  index,
  slug,
  onClose,
  onIndexChange,
}: {
  photos: ClientPhoto[];
  index: number;
  slug: string;
  onClose: () => void;
  onIndexChange: (next: number) => void;
}) {
  const photo = photos[index];
  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') onClose();
      else if (e.key === 'ArrowLeft' && index > 0) onIndexChange(index - 1);
      else if (e.key === 'ArrowRight' && index < photos.length - 1)
        onIndexChange(index + 1);
    }
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [index, photos.length, onClose, onIndexChange]);
  if (!photo) return null;
  const rawUrl = clientPhotoRawUrl(slug, photo.id);
  const hasPrev = index > 0;
  const hasNext = index < photos.length - 1;
  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label={`Photo ${index + 1} of ${photos.length}`}
      className="fixed inset-0 z-50 flex items-center justify-center"
      style={{ background: 'rgba(15, 23, 32, 0.92)' }}
      onClick={onClose}
    >
      <button
        type="button"
        onClick={(e) => {
          e.stopPropagation();
          onClose();
        }}
        aria-label="Close lightbox (Esc)"
        className="absolute top-4 right-4 w-10 h-10 rounded-full flex items-center justify-center text-white text-2xl leading-none focus:outline-none focus:ring-2 focus:ring-white/50"
        style={{ background: 'rgba(255,255,255,0.10)' }}
      >
        ×
      </button>
      <div className="absolute top-4 left-4">
        <p className="text-[11px] uppercase tracking-wider text-white/60 num">
          {index + 1} / {photos.length}
        </p>
      </div>
      {hasPrev && (
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            onIndexChange(index - 1);
          }}
          aria-label="Previous photo"
          className="absolute left-4 top-1/2 -translate-y-1/2 w-12 h-12 rounded-full flex items-center justify-center text-white text-3xl leading-none focus:outline-none focus:ring-2 focus:ring-white/50"
          style={{ background: 'rgba(255,255,255,0.10)' }}
        >
          ‹
        </button>
      )}
      {hasNext && (
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            onIndexChange(index + 1);
          }}
          aria-label="Next photo"
          className="absolute right-4 top-1/2 -translate-y-1/2 w-12 h-12 rounded-full flex items-center justify-center text-white text-3xl leading-none focus:outline-none focus:ring-2 focus:ring-white/50"
          style={{ background: 'rgba(255,255,255,0.10)' }}
        >
          ›
        </button>
      )}
      <img
        src={rawUrl}
        alt="Photo"
        onClick={(e) => e.stopPropagation()}
        className="max-w-[92vw] max-h-[88vh] object-contain"
        style={{ boxShadow: '0 20px 60px rgba(0,0,0,0.5)' }}
      />
    </div>
  );
}

// ============================================================
// Shape mappers — D1 row → local SQLite row
// ============================================================

function mapProjectDetail(p: PortalProject, slug: string): ProjectDetail {
  return {
    id: p.id,
    slug,
    name: p.name,
    project_type: (p.project_type as ProjectType) ?? 'ti_buildout',
    template_id: null,
    client_name: p.client_name,
    client_email: null,
    client_phone: null,
    address: null,
    city_state: p.city_state,
    zip: null,
    parcel_number: null,
    current_zone: null,
    overall_status: p.overall_status,
    target_completion_date: p.target_completion_date,
    actual_completion_date: null,
    construction_value: p.construction_value,
    contract_sum_current: null,
    consulting_fee: null,
    fee_structure: null,
    show_construction_value_to_client: p.construction_value_visible,
    show_target_date_to_client: p.target_completion_visible,
    show_status_banner_to_client: p.status_banner_visible,
    client_pin_configured: p.client_pin_configured,
    documents_root_path: null,
    private_scratchpad: null,
    color_tag: (p.color_tag as ColorTag) ?? 'sage',
    created_at: p.last_published_at,
    updated_at: p.last_published_at,
  } as ProjectDetail;
}

function mapPhaseSummary(p: PortalPhase): PhaseSummary {
  return {
    id: p.id,
    phase_number: p.phase_number,
    phase_name: p.phase_name,
    phase_category: p.phase_category,
    status: p.status,
    percent_complete: p.percent_complete,
    folder_path: null,
    is_visible_to_client: p.is_visible_to_client,
    target_start_date: p.target_start_date,
    target_end_date: p.target_end_date,
    default_duration_weeks: null,
    published_notes: p.published_notes,
  };
}

function mapTask(t: PortalTask, projectId: string): Task {
  return {
    id: t.id,
    phase_id: t.phase_id,
    project_id: projectId,
    task_number: t.task_number,
    task_text: t.task_text,
    task_description: t.task_description,
    task_group: t.task_group,
    document_id: null,
    familiarity_level: null,
    is_complete: t.is_complete,
    is_critical: t.is_critical,
    is_client_visible: t.is_client_visible,
    published_note: t.published_note ?? '',
    due_date: t.due_date,
    completed_date: t.completed_date,
    has_unpublished_changes: 0,
    // The published subset doesn't carry created_at/updated_at; we don't
    // surface either in portal-mode renders, but the type requires them.
    // Use last_published_at as a stable stand-in.
    created_at: '',
    updated_at: '',
  };
}

function mapActivityEntry(
  e: PortalActivityEntry,
  projectId: string,
): ActivityLogEntry {
  return {
    id: e.id,
    project_id: projectId,
    phase_id: e.phase_id,
    task_id: e.task_id,
    section_key: e.section_key,
    permit_id: e.permit_id,
    pay_app_id: null,
    // LogType is a union string on the main client; cast-through is safe
    // here — the portal renders the log_type as a plain string in chips.
    log_type: e.log_type as ActivityLogEntry['log_type'],
    contact_id: null,
    contact_name_text: e.contact_name_text,
    event_timestamp: e.event_timestamp,
    private_entry: '',
    published_summary: e.published_summary,
    is_published: e.is_published,
    manual_sort_order: e.manual_sort_order,
    has_unpublished_changes: 0,
    is_urgent: e.is_urgent,
    entry_title: e.entry_title,
    created_at: e.event_timestamp,
    updated_at: e.event_timestamp,
  };
}

function synthesizeCounts(
  activityCount: number,
  tasksCount: number,
  permitsCount: number,
  pmInternalCountsJson: string | null,
): Record<string, number> {
  // D-134 — counts strip now reflects "behind-scenes work volume" per
  // memory feedback_portal_shows_overview_counts.md. The categories that
  // exist in the published subset (tasks, permits, activity_log) come from
  // the actual row counts pushed to D1; the categories that DON'T push
  // (notes, tracked_documents, tracked_plans, pay_apps) come from the
  // count-only metadata the publisher stamps onto the project row. If
  // pmInternalCountsJson is missing/malformed (project published before
  // the migration), those four fall back to 0.
  let pmInternal: Record<string, unknown> = {};
  if (pmInternalCountsJson) {
    try {
      pmInternal = JSON.parse(pmInternalCountsJson);
    } catch {
      pmInternal = {};
    }
  }
  const intOr0 = (v: unknown) => (typeof v === 'number' && Number.isFinite(v) ? v : 0);
  return {
    notes: intOr0(pmInternal.notes),
    tasks: tasksCount,
    tracked_documents: intOr0(pmInternal.tracked_documents),
    tracked_plans: intOr0(pmInternal.tracked_plans),
    permits: permitsCount,
    pay_apps: intOr0(pmInternal.pay_apps),
    activity_log: activityCount,
  };
}

// ============================================================
// Centered message — used for loading / not-found / error states
// ============================================================
function CenteredMessage({
  title,
  children,
}: {
  title: string;
  children: React.ReactNode;
}) {
  return (
    <div className="min-h-screen flex items-center justify-center bg-[var(--color-bg)] px-4">
      <div className="bg-[var(--color-surface)] border border-[var(--color-border)] rounded-lg shadow-sm p-10 max-w-md w-full text-center">
        <h1 className="text-xl font-bold text-[var(--color-ink)]">{title}</h1>
        <div className="mt-2 text-sm text-[var(--color-ink-2)]">{children}</div>
      </div>
    </div>
  );
}
